import { useEffect, useMemo, useState } from "react";
import AdminCard from "../components/admin/AdminCard";
import AdminTable from "../components/admin/AdminTable";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Loader from "../components/common/Loader";
import * as userService from "../services/userService";

const safeCallGetAllUsers = async () => {
  if (typeof userService.getAllUsers === "function") {
    return userService.getAllUsers();
  }

  if (typeof userService.getUsers === "function") {
    return userService.getUsers();
  }

  throw new Error("User list method was not found in userService.");
};

const safeCallDeleteUser = async (id) => {
  if (typeof userService.deleteUser === "function") {
    return userService.deleteUser(id);
  }

  if (typeof userService.removeUser === "function") {
    return userService.removeUser(id);
  }

  throw new Error("Delete user method was not found in userService.");
};

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await safeCallGetAllUsers();
      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Failed to load users.");
      }

      setUsers(Array.isArray(data?.userList) ? data.userList : []);
    } catch (err) {
      setError(err?.message || "We could not load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesRole = roleFilter === "ALL" ? true : user?.role === roleFilter;

      const fullText = `${user?.firstname || ""} ${user?.lastname || ""} ${
        user?.email || ""
      } ${user?.role || ""}`.toLowerCase();

      const matchesSearch = !query || fullText.includes(query);

      return matchesRole && matchesSearch;
    });
  }, [users, search, roleFilter]);

  const resetMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      setActionLoading(true);
      resetMessages();

      const response = await safeCallDeleteUser(deleteTarget.id);
      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Failed to delete user.");
      }

      setSuccessMessage(data?.message || "The user account was deleted successfully.");
      setDeleteTarget(null);

      await loadUsers();
    } catch (err) {
      setError(err?.message || "We could not delete the user account.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading ? <Loader text="Loading user records..." /> : null}

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-medium text-green-700">{successMessage}</p>
        </div>
      ) : null}

      <AdminCard
        title="Manage Users"
        subtitle="Review client and admin accounts, open profile details, and remove records when necessary."
        actions={
          <div className="flex flex-col gap-3 md:flex-row">
            <Input
              name="userSearch"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, email, or role..."
              className="min-w-[240px]"
            />

            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              className="min-h-[46px] rounded-xl border border-goldlight bg-white px-4 py-3 text-sm text-[#2f2626]"
            >
              <option value="ALL">All Roles</option>
              <option value="ADMIN">ADMIN</option>
              <option value="CLIENT">CLIENT</option>
            </select>
          </div>
        }
      >
        <div className="max-h-[520px] overflow-y-auto pr-1">
          <AdminTable
            loading={loading}
            data={filteredUsers}
            emptyMessage="No users match the current filters."
            columns={[
              {
                key: "name",
                label: "Name",
                render: (row) =>
                  `${row?.firstname || ""} ${row?.lastname || ""}`.trim() || "—",
              },
              {
                key: "email",
                label: "Email",
                render: (row) => row?.email || "—",
              },
              {
                key: "phoneNumber",
                label: "Phone",
                render: (row) => row?.phoneNumber || "—",
              },
              {
                key: "role",
                label: "Role",
                render: (row) => row?.role || "—",
              },
              {
                key: "emailVerified",
                label: "Verified",
                render: (row) =>
                  row?.emailVerified ? (
                    <span className="status-chip status-chip-confirmed">YES</span>
                  ) : (
                    <span className="status-chip status-chip-canceled">NO</span>
                  ),
              },
            ]}
            actions={(row) => [
              {
                label: "View",
                variant: "secondary",
                onClick: () => setSelectedUser(row),
              },
              {
                label: "Delete",
                variant: "danger",
                onClick: () => setDeleteTarget(row),
              },
            ]}
          />
        </div>
      </AdminCard>

      <Modal
        isOpen={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        title="User Details"
        size="md"
        footer={
          <div className="flex justify-end">
            <Button type="button" variant="secondary" onClick={() => setSelectedUser(null)}>
              Close
            </Button>
          </div>
        }
      >
        {selectedUser ? (
          <div className="space-y-5">
            <div className="form-grid two">
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">First Name</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedUser?.firstname || "—"}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Last Name</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedUser?.lastname || "—"}
                </p>
              </div>
            </div>

            <div className="form-grid two">
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Email</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedUser?.email || "—"}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Phone</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedUser?.phoneNumber || "—"}
                </p>
              </div>
            </div>

            <div className="form-grid two">
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Role</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedUser?.role || "—"}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Email Verified</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedUser?.emailVerified ? "Yes" : "No"}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#3b2f2f]">Bookings Count</p>
              <p className="mt-2 text-sm text-muted-warm">
                {Array.isArray(selectedUser?.bookings)
                  ? selectedUser.bookings.length
                  : 0}
              </p>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => {
          if (!actionLoading) setDeleteTarget(null);
        }}
        title="Delete User"
        size="sm"
        footer={
          <div className="flex flex-col gap-3 md:flex-row md:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>

            <Button
              type="button"
              variant="danger"
              loading={actionLoading}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-sm leading-6 text-muted-warm">
          Are you sure you want to delete this user account?
        </p>
      </Modal>
    </div>
  );
};

export default ManageUsers;